import {
  AlertTriangle,
  Check,
  CircleDashed,
  CircleHelp,
  Minus,
  Pin,
  type LucideIcon,
} from 'lucide-react';

import type { FieldSets } from '../types/run-detail.api.types';

/** Products each field was enriched in — `coverage.byField[field]` when content-gen reported it. */
function coverageOf(coverage: Record<string, unknown> | undefined, field: string): number | null {
  const byField = coverage?.byField;
  if (!byField || typeof byField !== 'object') return null;
  const value = (byField as Record<string, unknown>)[field];
  return typeof value === 'number' ? value : null;
}

/** One matrix cell: ✓ yes, — no, ⚠ a problem (sent but nothing written), ? not reported, 📌
 *  always written for this destination, ◌ no restriction requested (`honoured: null` — never a
 *  verified ✓). */
export type CellState = 'yes' | 'no' | 'problem' | 'unknown' | 'alwaysOn' | 'unrestricted';

/** Glyph + tone per matrix cell. */
export const CELL_ICON: Record<CellState, { Icon: LucideIcon; tone: string }> = {
  yes: { Icon: Check, tone: 'text-foreground' },
  no: { Icon: Minus, tone: 'text-muted-foreground/60' },
  problem: { Icon: AlertTriangle, tone: 'text-warning' },
  unknown: { Icon: CircleHelp, tone: 'text-muted-foreground/60' },
  alwaysOn: { Icon: Pin, tone: 'text-info' },
  unrestricted: { Icon: CircleDashed, tone: 'text-muted-foreground/60' },
};

/**
 * What the "Sends" column can honestly claim (catalog-service §90, A6/A7): `plan` on an older
 * backend (no `deliveredBasis`, today's wording), `requested` while the list is only the request
 * (before DELIVERY or while the push is unconfirmed), `delivered` once integrations confirmed the
 * push. Integrations confirms a push, never a field — no per-field receipt exists either way.
 */
export type SendsBasis = 'plan' | 'requested' | 'delivered';

/** The field sets' `SendsBasis` — `deliveryConfirmed: true` wins, else the list is the request.
 *  `plan` is no longer produced: every deployed backend sends `deliveredBasis` (it is required). */
export function sendsBasisOf(data: FieldSets): SendsBasis {
  return data.deliveryConfirmed === true ? 'delivered' : 'requested';
}

/** The locale suffix for a `SendsBasis` ('' keeps today's keys on an older backend). */
export function sendsKeySuffix(basis: SendsBasis): '' | 'Requested' | 'Delivered' {
  if (basis === 'delivered') return 'Delivered';
  return basis === 'requested' ? 'Requested' : '';
}

/** One field's row in the matrix — every state the field sets carry, mapped to the columns. */
export interface MatrixRow {
  field: string;
  /** `requested` — the operator selected it. */
  asked: CellState;
  /** `honoured` (null = no restriction requested → `unrestricted`, never a ✓) / `alwaysOn`. */
  accepts: CellState;
  /** `delivered` — delivery ships it to this destination. */
  sent: CellState;
  /** `enriched` (null = not reported) — content generation populated it; ⚠ when sent but not. */
  written: CellState;
  /** `coverage.byField[field]` — products it was written in. */
  coverage: number | null;
  /** Row note: 'attention' (sent, nothing written), 'writtenNot*' (written, not shipped), 'dropped'. */
  note: 'attention' | 'writtenNotRequested' | 'writtenNotSent' | 'dropped' | null;
}

/** Builds one field's matrix row from the field sets (`requested` / `honoured` / `alwaysOn` /
 *  `delivered` / `enriched` / `coverage`) — the single place the columns are derived. */
export function matrixRow(data: FieldSets, field: string): MatrixRow {
  const requested = data.requested.includes(field);
  const delivered = data.delivered.includes(field);
  const alwaysOn = data.alwaysOn?.includes(field) ?? false;
  const unrestricted = data.honoured === null;
  const honoured = data.honoured?.includes(field) ?? false;
  const coverage = coverageOf(data.coverage, field);
  const enrichedKnown = data.enriched !== null;
  const enriched = data.enriched?.includes(field) ?? false;
  const nothingWritten = enrichedKnown && (!enriched || coverage === 0);
  const attention = delivered && nothingWritten;
  return {
    field,
    asked: requested ? 'yes' : 'no',
    accepts: alwaysOn ? 'alwaysOn' : unrestricted ? 'unrestricted' : honoured ? 'yes' : 'no',
    sent: delivered ? 'yes' : 'no',
    written: !enrichedKnown ? 'unknown' : attention ? 'problem' : enriched ? 'yes' : 'no',
    coverage,
    note: attention
      ? 'attention'
      : enriched && !delivered
        ? requested
          ? 'writtenNotSent'
          : 'writtenNotRequested'
        : requested && !delivered
          ? 'dropped'
          : null,
  };
}
